import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  generalInfoSummaryQuery,
  consolidatedScoresSummaryQuery,
  scoresStatsQuery
} from './api';

import { popupMessagesSummary } from './data';

import PerformanceRankingInfo from '../UI/performanceRankingInfo';
import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Summary from '../UI/game/summary';

export default function ThePrinciplesSummary() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentThePrinciplesGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentThePrinciplesGameId();

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);
  const [rank, setRank] = useState('');

  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [firstAttemptPoints, setFirstAttemptPoints] = useState(0);
  const [firstAttemptAccuracy, setFirstAttemptAccuracy] = useState(0);
  const [firstAttemptAverageTime, setFirstAttemptAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedScores } = useQuery(consolidatedScoresSummaryQuery());
  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId }));

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  const principlesScores = useMemo(() => consolidatedScores?.principles || [], [consolidatedScores]);

  const currentSessionScores = useMemo(() => {
    if (!gamesSessions || !principlesScores.length) return null;

    const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
    const currentGameSession = currentGameSessions.find(session => !session.completed)
      || currentGameSessions.filter(session => session.completed).reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);

    return principlesScores.find(s => s.game === gameId && s.game_session === currentGameSession?.id);
  }, [gamesSessions, principlesScores, gameId, user?.id]);

  const firstAttemptSessionScores = useMemo(() => {
    return principlesScores.find(s => s.game === gameId && s.attempt === 1);
  }, [principlesScores, gameId]);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);

      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = currentGameSessions
          .filter(session => session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(currentSessionScores && scoresStats) {
      const summary = currentSessionScores.session_summary;
      const totalPoints = summary.total_points_session;
      const totalAccuracy = summary.total_accuracy_session;

      const levels = currentSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;

      setPoints(totalPoints);
      setAccuracy(totalAccuracy);
      setAverageTime(avgTime);

      // Rank is computed server-side and exposed as session.rank_session,
      // matching what /the-principles/badge displays. Reading it from
      // the same source keeps the summary flow in sync with the badge.
      setRank(scoresStats.session?.rank_session || '');
      setDataLoaded(true);
    }
  }, [currentSessionScores, scoresStats]);

  useEffect(() => {
    if(firstAttemptSessionScores) {
      const summary = firstAttemptSessionScores.session_summary;
      setFirstAttemptPoints(summary.total_points_session);
      setFirstAttemptAccuracy(summary.total_accuracy_session);

      const levels = firstAttemptSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;
      setFirstAttemptAverageTime(avgTime);
    }
  }, [firstAttemptSessionScores]);

  const handlePopupMessagesNextButtonClick = () => {
    navigate('/the-principles/summary/assumptions-principles-constraints');
  }

  const popupMessagesFormatted = useMemo(() => {
    let formattedMessages = currentGameSessionAttempt > 1
      ? popupMessagesSummary
      : [
          {
            ...popupMessagesSummary[0],
            text: 'Congratulations on completing the exercise!<br><br>' + popupMessagesSummary[0].text
          },
          ...popupMessagesSummary.slice(1)
        ];

    if(formattedMessages) {
      formattedMessages = formattedMessages.map(message => ({
        ...message,
        text: message.text.replace('{accuracy}', `${(accuracy * 100).toFixed(2)}%`)
          .replace('{averageTime}', `${averageTime} seconds`)
          .replace('{points}', points.toLocaleString('en-US'))
          .replace('{rank}', rank)
      }));
    }

    return formattedMessages;
  }, [currentGameSessionAttempt, accuracy, averageTime, points, rank]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }

  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessagesFormatted}
              buttonText="Next"
              messageSize="small"
              onButtonClick={handlePopupMessagesNextButtonClick}
            />
            <Summary
              rank={rank}
              points={points}
              firstAttemptPoints={firstAttemptPoints}
              lines={[
                {label: 'Accuracy', value: accuracy, firstAttemptValue: firstAttemptAccuracy},
                {label: 'Average Time', value: averageTime, firstAttemptValue: firstAttemptAverageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
